import React, { useState, useEffect, useRef } from 'react';
import { FiMessageSquare, FiX, FiUser, FiClock, FiEdit } from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import NewMessageModal from './NewMessageModal';
import './DMInbox.css';

/**
 * DMInbox Component
 * Displays navigation header icon with unread badge and conversation list dropdown
 * Features:
 * - Real-time unread message count badge
 * - Conversation list with last message preview
 * - Timestamp for each conversation
 * - Unread indicator
 * - Opens DirectMessaging window on conversation click
 * - Supabase real-time subscriptions for live updates
 */
export default function DMInbox({ onOpenConversation }) {
    const { currentUser, supabase } = useAuth();
    const [isOpen, setIsOpen] = useState(false);
    const [conversations, setConversations] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [showNewMessageModal, setShowNewMessageModal] = useState(false);
    const dropdownRef = useRef(null);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [isOpen]);

    // Fetch conversations (N+1 QUERY FIX: Using optimized RPC)
    const fetchConversations = async () => {
        if (!currentUser?.id) return;

        setLoading(true);
        try {
            // Single optimized query that fetches everything at once
            const { data: conversationData, error } = await supabase.rpc('get_inbox_conversations', {
                p_limit: 50,
                p_offset: 0
            });

            if (error) throw error;

            if (!conversationData || conversationData.length === 0) {
                setConversations([]);
                setUnreadCount(0);
                return;
            }

            // Transform RPC results to match component format
            const conversationList = conversationData
                .filter(conv => conv.last_message_id !== null) // Only show conversations with messages
                .map(conv => ({
                    partner: {
                        id: conv.partner_id,
                        username: conv.partner_username || 'Unknown User',
                        display_name: conv.partner_display_name,
                        banned: conv.partner_banned
                    },
                    lastMessage: {
                        id: conv.last_message_id,
                        content: conv.last_message_content,
                        sender_id: conv.last_message_sender_id,
                        created_at: conv.last_message_created_at,
                        is_read: conv.last_message_read
                    },
                    unreadCount: parseInt(conv.unread_count) || 0,
                    conversationId: conv.conversation_id,
                    totalMessageCount: parseInt(conv.total_message_count) || 0
                }));

            // Calculate total unread count
            const totalUnread = conversationList.reduce((sum, conv) => sum + conv.unreadCount, 0);

            setConversations(conversationList);
            setUnreadCount(totalUnread);
        } catch (error) {
            console.error('[DMInbox] Error fetching conversations:', error);
        } finally {
            setLoading(false);
        }
    };

    // Initial load
    useEffect(() => {
        if (currentUser?.id) {
            fetchConversations();
        }
    }, [currentUser?.id]);

    // Real-time subscription
    useEffect(() => {
        if (!currentUser?.id) return;

        const channel = supabase
            .channel('dm-inbox-updates')
            .on(
                'postgres_changes',
                {
                    event: '*',
                    schema: 'public',
                    table: 'dm_messages',
                    filter: `recipient_id=eq.${currentUser.id}`
                },
                (payload) => {
                    console.log('DM update received:', payload);
                    fetchConversations(); // Refresh conversation list
                }
            )
            .subscribe();

        return () => {
            supabase.removeChannel(channel);
        };
    }, [currentUser?.id]);

    const handleToggle = () => {
        setIsOpen(!isOpen);
        if (!isOpen && currentUser?.id) {
            fetchConversations(); // Refresh when opening
        }
    };

    const handleConversationClick = (conversation) => {
        setIsOpen(false);
        if (onOpenConversation) {
            onOpenConversation(conversation.partner);
        }
    };

    const handleNewMessage = () => {
        setShowNewMessageModal(true);
        setIsOpen(false);
    };

    const handleSelectUser = (user) => {
        setShowNewMessageModal(false);
        if (onOpenConversation) {
            onOpenConversation(user);
        }
    };

    const formatTimestamp = (timestamp) => {
        const date = new Date(timestamp);
        const now = new Date();
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMins < 1) return 'Just now';
        if (diffMins < 60) return `${diffMins}m ago`;
        if (diffHours < 24) return `${diffHours}h ago`;
        if (diffDays < 7) return `${diffDays}d ago`;

        return date.toLocaleDateString();
    };

    const truncateMessage = (text, maxLength = 50) => {
        if (!text) return '';
        return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
    };

    if (!currentUser) return null;

    return (
        <div className="dm-inbox-container" ref={dropdownRef}>
            {/* Header Icon with Badge */}
            <button
                className="dm-inbox-icon-btn"
                onClick={handleToggle}
                aria-label="Direct Messages"
            >
                <FiMessageSquare className="dm-icon" />
                {unreadCount > 0 && (
                    <span className="dm-unread-badge">
                        {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                )}
            </button>

            {/* Dropdown/Modal */}
            {isOpen && (
                <div className="dm-inbox-dropdown">
                    {/* Header */}
                    <div className="dm-inbox-header">
                        <h3>
                            <FiMessageSquare />
                            Messages
                        </h3>
                        <div className="dm-header-actions">
                            <button
                                className="dm-new-message-btn"
                                onClick={handleNewMessage}
                                title="New Message"
                            >
                                <FiEdit />
                            </button>
                            <button className="dm-close-btn" onClick={() => setIsOpen(false)}>
                                <FiX />
                            </button>
                        </div>
                    </div>

                    {/* Conversation List */}
                    <div className="dm-inbox-content">
                        {loading ? (
                            <div className="dm-loading">
                                <div className="dm-spinner"></div>
                                <p>Loading conversations...</p>
                            </div>
                        ) : conversations.length === 0 ? (
                            <div className="dm-empty-state">
                                <FiMessageSquare className="empty-icon" />
                                <h4>No messages yet</h4>
                                <p>Start a conversation from a marketplace listing or user profile</p>
                            </div>
                        ) : (
                            <div className="dm-conversation-list">
                                {conversations.map((conv) => (
                                    <div
                                        key={conv.partner.id}
                                        className={`dm-conversation-item ${conv.unreadCount > 0 ? 'unread' : ''}`}
                                        onClick={() => handleConversationClick(conv)}
                                    >
                                        {/* Unread Indicator */}
                                        {conv.unreadCount > 0 && (
                                            <div className="unread-dot"></div>
                                        )}

                                        {/* Avatar/Icon */}
                                        <div className="conversation-avatar">
                                            <FiUser />
                                        </div>

                                        {/* Conversation Info */}
                                        <div className="conversation-info">
                                            <div className="conversation-header">
                                                <span className="partner-name">
                                                    {conv.partner.display_name || conv.partner.username}
                                                </span>
                                                <span className="conversation-time">
                                                    <FiClock />
                                                    {formatTimestamp(conv.lastMessage.created_at)}
                                                </span>
                                            </div>
                                            <div className="last-message">
                                                {conv.lastMessage.sender_id === currentUser.id && (
                                                    <span className="you-label">You: </span>
                                                )}
                                                <span className={conv.unreadCount > 0 ? 'unread-text' : ''}>
                                                    {truncateMessage(conv.lastMessage.content)}
                                                </span>
                                            </div>
                                        </div>

                                        {/* Unread Count Badge */}
                                        {conv.unreadCount > 0 && (
                                            <div className="conversation-unread-badge">
                                                {conv.unreadCount}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* New Message Modal */}
            {showNewMessageModal && (
                <NewMessageModal
                    onClose={() => setShowNewMessageModal(false)}
                    onSelectUser={handleSelectUser}
                />
            )}
        </div>
    );
}
